import { type TeamsBindLink, parseTeamsBindLink } from "./helpers";

// ---------------------------------------------------------------------------
// teamsBindingActions — the two channel-scoped Teams mutations, in one place
// ---------------------------------------------------------------------------

/**
 * Both the actor Identity tab (`ChannelManager`) and the channel rail's
 * CONNECTIONS section bind and unbind the same channel through the same two
 * endpoints. They live here rather than being inlined twice because a second
 * copy is how two surfaces quietly start disagreeing -- about the error
 * message, about which status codes are worth distinguishing, about whether a
 * failed call still clears the link.
 *
 * The endpoints are org-admin only and reject private channels, so callers get
 * a discriminated result instead of a thrown string: the rail turns `forbidden`
 * into an explanatory line rather than a generic failure.
 */

export type TeamsBindingFailure =
  | { kind: "disabled" }
  | { kind: "failed"; status: number }
  | { kind: "forbidden" }
  | { kind: "private_channel" };

export type MintBindLinkResult =
  | { ok: false; failure: TeamsBindingFailure }
  | { ok: true; link: TeamsBindLink };

export type UnbindResult =
  | { ok: false; failure: TeamsBindingFailure }
  | { ok: true };

function classifyFailure(status: number): TeamsBindingFailure {
  if (status === 401 || status === 403) {
    // The API answers 403 both for a non-admin and for an org without the
    // Teams chat SDK; the caller's own gate already covers the latter, so the
    // actionable reading here is "you are not allowed".
    return { kind: "forbidden" };
  }
  if (status === 409) {
    return { kind: "private_channel" };
  }
  return { kind: "failed", status };
}

export function describeTeamsBindingFailure(
  failure: TeamsBindingFailure,
): string {
  switch (failure.kind) {
    case "disabled": {
      return "Teams is not connected for this organization.";
    }
    case "failed": {
      return `Request failed (${failure.status}).`;
    }
    case "forbidden": {
      return "Only an org admin can change this.";
    }
    case "private_channel": {
      return "Private channels cannot be Teams-bound.";
    }
  }
}

export async function mintTeamsBindLink(
  channelId: string,
): Promise<MintBindLinkResult> {
  const response = await fetch(
    `/user/channels/${channelId}/teams-binding-token`,
    { credentials: "include", method: "POST" },
  );
  if (!response.ok) {
    return { failure: classifyFailure(response.status), ok: false };
  }
  const link = parseTeamsBindLink(await response.json());
  if (link === null) {
    // A 200 with no usable url is a server contract violation, not a user
    // error: surfacing it as a plain failure beats rendering an empty panel.
    return { failure: { kind: "failed", status: response.status }, ok: false };
  }
  return { link, ok: true };
}

export async function unbindTeamsChannel(
  channelId: string,
): Promise<UnbindResult> {
  const response = await fetch(
    `/user/channels/${channelId}/teams-binding:unbind`,
    { credentials: "include", method: "POST" },
  );
  if (!response.ok) {
    return { failure: classifyFailure(response.status), ok: false };
  }
  return { ok: true };
}
